<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header"
               v-text="$t('messages.sign_up')">
          </div>

          <div class="card-body">
            <validation-observer ref="signupForm"
                                 tag="form"
                                 v-slot="{ invalid }"
                                 @submit.prevent="processRegistration">

              <div class="form-group row">
                <label for="username"
                       class="col-md-4 col-form-label text-md-right"
                       v-text="$t('messages.username')">
                </label>

                <div class="col-md-6">
                  <validation-provider ref="username_validation"
                                       :name="$t('messages.username')"
                                       rules="required|max:255"
                                       v-slot="{ errors }">
                    <input id="username"
                           type="text"
                           :class="['form-control', {'is-invalid': errors.length > 0}]"
                           name="username"
                           autocomplete="off"
                           :readonly="loading"
                           v-model="formData.username"
                    />

                    <span class="invalid-feedback" role="alert" v-if="errors.length > 0">
                      <strong v-text="errors[0]"></strong>
                    </span>
                  </validation-provider>
                </div>
              </div>
              <!--/ Username group -->

              <div class="form-group row">
                <label for="email"
                       class="col-md-4 col-form-label text-md-right"
                       v-text="$t('messages.email')">
                </label>

                <div class="col-md-6">
                  <validation-provider ref="email_validation"
                                       :name="$t('messages.email')"
                                       rules="required|max:255|email"
                                       v-slot="{ errors }">
                    <input id="email"
                           type="email"
                           :class="['form-control', {'is-invalid': errors.length > 0}]"
                           name="email"
                           autocomplete="off"
                           :readonly="loading"
                           v-model="formData.email"
                    />

                    <span class="invalid-feedback" role="alert" v-if="errors.length > 0">
                      <strong v-text="errors[0]"></strong>
                    </span>
                  </validation-provider>
                </div>
              </div>
              <!--/ Email-->

              <div class="flex flex-row" v-show="false">
                <div class="col-md-4"></div>
                <div class="col-md-8 pl-1">
                  <validation-provider ref="is_adult_validation"
                                       name="IsAdult"
                                       rules="required:true"
                                       v-slot="{ errors }">
                      <div class="flex flex-row items-center">
                        <input id="IsAdult"
                              type="checkbox"
                              :class="['form-checkbox w-5', {'is-invalid': errors.length > 0}]"
                              name="is_adult"
                              v-model="formData.is_adult"
                        />

                        <label for="is_adult"
                               class="m-0 ml-4"
                               v-text="$t('messages.i_am_at_least_18')">
                        </label>
                      </div>

                      <span class="w-full mt-1 text-sm text-feedback-red" role="alert" v-if="errors.length > 0">
                        <strong v-text="errors[0]"></strong>
                      </span>
                  </validation-provider>
                </div>
              </div>
              <!--/ Over 18 -->

              <div class="flex flex-row" v-show="false">
                <div class="col-md-4"></div>
                <div class="col-md-8 pl-1">
                  <validation-provider ref="reside_in_us_validation"
                                       name="ResideInUs"
                                       rules="required:true"
                                       v-slot="{ errors }">
                      <div class="flex flex-row items-center">
                        <input id="ResideInUs"
                               type="checkbox"
                               :class="['form-checkbox w-5', {'is-invalid': errors.length > 0}]"
                               name="reside_in_us"
                               v-model="formData.reside_in_us"
                        />

                        <label for="reside_in_us"
                               class="m-0 ml-4"
                               v-text="$t('messages.i_reside_in_us')">
                        </label>
                      </div>

                    <span class="w-full mt-1 text-sm text-feedback-red" role="alert" v-if="errors.length > 0">
                      <strong v-text="errors[0]"></strong>
                    </span>
                  </validation-provider>
                </div>
              </div>
              <!-- Reside in us -->

              <div class="form-group row mt-2 mb-0">

                <div class="col-md-8 mx-auto lg:ml-4">
                  <button type="submit"
                          class="bg-green-500 w-full lg:w-1/4 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                          :class="{'cursor-not-allowed opacity-75': (invalid || loading)}"
                          :disabled="invalid || loading">
                    <span
                      v-if="!loading"
                      v-text="$t('messages.sign_up')">
                    </span>

                    <pulse-loader :loading="loading"
                                 color="#ffffff"
                                 size="10px">
                    </pulse-loader>
                  </button>
                </div>
              </div>
            </validation-observer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {register} from '@/api/auth';
import {applyTo} from '@/utils/validation';
import {isValidationError} from '@/utils/response';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

export default {
  components: {
    PulseLoader
  },

  data() {
    return {
      loading: false,
      formData: {
        username: null,
        email: null,
        is_adult: true,
        reside_in_us: true
      }
    }
  },

  metaInfo() {
    return {
      title: this.$t('messages.sign_up'),
    }
  },

  methods: {
    /**
     * Handle error.
     *
     * @param error
     */
    handleError(error) {
      if (isValidationError(error)) {
        applyTo({vm: this, errors: error.response.data.errors});
      }
    },

    /**
     * Process registration of the User.
     */
    async processRegistration() {
      try {
        let isValid = await this.$refs.signupForm.validate();

        if (isValid) {
          this.loading = true;
          let {data: {registered, redirect}} = await register(this.formData);

          if (registered) {
            window.location = redirect;
          }
        }
      } catch (error) {
        this.handleError(error);
      }

      this.loading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/bootstrap';
</style>
